import { useMemo } from 'react';
import { ODataDsnsService } from '../services/ODataDsnsService';
import { useLogger } from '../context/useLogger';
import { useAccessToken } from './useAccessToken';
import { useQuery } from '@tanstack/react-query';
import { DsnBilansResponse } from '../types/bilan.types';

/**
 * Fetches a single bilan by its own instance ID, independently of the paginated
 * bilans list, so a bilan detail page can be resolved from a direct/shared link.
 *
 * @param bilanId - The unique identifier of the bilan to fetch.
 */
export function useQueryBilanDetail(bilanId: string | undefined) {
  const getAccessToken = useAccessToken();
  const logger = useLogger();

  const odataService = useMemo(
    () => new ODataDsnsService(getAccessToken, logger),
    [getAccessToken, logger],
  );

  const fetchBilanDetails = () => {
    if (!bilanId) {
      throw new Error('Bilan ID is required');
    }
    const response: Promise<DsnBilansResponse> = odataService.fetchDsnBilanDetails(bilanId);

    return response;
  };

  logger.info('useQueryBilanDetail bilanId:', bilanId);

  const queryKey = ['query-get', 'BilanDetail', bilanId];
  const queryResult = useQuery({
    queryKey,
    queryFn: fetchBilanDetails,
    enabled: !!bilanId,
    throwOnError: (_error, query) => query.state.data === undefined,
  });

  return queryResult;
}
